import cx from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon, { IconType } from "../Icon/Icon";
import "./Accordion.scss";

export interface AccordionProps {
  title: string;
  children: any;
}

const Accordion = (props: AccordionProps) => {
  const { title, children } = props;

  // States

  const contentRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  const [opened, setOpened] = useState(false);

  // Memos

  const style = useMemo(() => {
    return {
      height: (opened && contentRef.current?.scrollHeight) || 0
    };
  }, [opened]);

  const contentProps = useMemo(() => {
    if (children?.innerHTML) {
      return null;
    }

    if (typeof children === "string") {
      return { dangerouslySetInnerHTML: { __html: children } };
    }

    return { children };
  }, [children]);

  // Callbacks

  const handleToggleOpenState = useCallback(() => {
    setOpened(!opened);
  }, [opened]);

  // Effects

  useEffect(() => {
    if (!children?.innerHTML) {
      return;
    }

    targetRef.current.childNodes.forEach(node => {
      targetRef.current.removeChild(node);
    });

    targetRef?.current?.appendChild(children);
  }, [opened, children]);

  // Variables

  const classes = cx("Accordion", opened && "Accordion--open");

  // Render

  /*
   * It would be better to use details and summary, but animation is not possible with those elements for now, and
   * they are not supported in IE11, nor in Edge.
   */
  return (
    <div className={classes}>
      <div className="Accordion__header" onClick={handleToggleOpenState}>
        <span className="Accordion__title">{title}</span>
        <Icon className={"Accordion__icon"} type={opened ? IconType.NAV_TOP : IconType.NAV_BOTTOM} />
      </div>

      <div className="Accordion__content-container" ref={contentRef} style={style}>
        <div className="Accordion__content" ref={targetRef} {...contentProps} />
      </div>
    </div>
  );
};

export default Accordion;
